import { addPromiseToCache, CacheState } from 'base/library_cache';
import { getFontFacesToLoad } from 'base/styleguide/fonts';
import { TypographyTarget } from 'base/styleguide/model';
import { Link } from 'base/ui/link';
import { BlocksService, FontFamilyMetadata } from 'blocks/blocks_service';
import { observable } from 'mobx';
import * as React from 'react';
import CleanImage from './font_collection_images/clean.file.svg';
import AyushCollectionImage from './font_collection_images/collection-ayush.file.webp';
import BrettCollectionImage from './font_collection_images/collection-brett.file.webp';
import DannCollectionImage from './font_collection_images/collection-dann.file.webp';
import FonsCollectionImage from './font_collection_images/collection-fons.file.webp';
import CompressedImage from './font_collection_images/compressed.file.svg';
import EdgyImage from './font_collection_images/edgy.file.svg';
import ElegantImage from './font_collection_images/elegant.file.svg';
import OGImage from './font_collection_images/og.file.svg';
import QuirkyImage from './font_collection_images/quirky.file.svg';
import RoundedImage from './font_collection_images/rounded.file.svg';
import StaffFavoritesImage from './font_collection_images/staff-favorites.file.svg';

type RemoteFontFace = {
  familySlug: string;
  faceSlug: string;
  weight: string;
  style: string;
};

const getCacheKey = (familySlug: string, preferredWeights: number[]) =>
  `${familySlug}-${preferredWeights.join(',')}` as const;

class RemoteFontStore {
  // Doesn't cache any data. Just used for tracking whether the font family has
  // been downloaded and is ready to use in CSS, and its loading state.
  private remoteFontFamilyCache: Map<`${string}-${string}`, CacheState<{}>> =
    observable.map([], {
      deep: false,
    });

  isRemoteFontFamilyLoaded(slug: string, preferredWeights: number[]) {
    return (
      this.remoteFontFamilyCache.get(getCacheKey(slug, preferredWeights))
        ?.type === 'fulfilled'
    );
  }

  isRemoteFontFamilyLoading(slug: string, preferredWeights: number[]) {
    const type = this.remoteFontFamilyCache.get(
      getCacheKey(slug, preferredWeights),
    )?.type;
    return type === undefined || type === 'pending';
  }

  loadRemoteFontFamily(
    slug: string,
    preferredWeights: number[],
    { blocksService }: { blocksService: BlocksService },
  ) {
    const cacheKey = getCacheKey(slug, preferredWeights);

    const cached = this.remoteFontFamilyCache.get(cacheKey);
    if (cached) {
      return cached;
    }

    return addPromiseToCache(
      this.remoteFontFamilyCache,
      cacheKey,
      (async () => {
        // TODO: cache font family metadata
        const fontFamily = await blocksService.getFontFamily(slug);

        await Promise.all(
          getFontFacesToLoad(fontFamily, preferredWeights).map((fontFace) =>
            this.loadRemoteFontFace({
              familySlug: slug,
              faceSlug: fontFace.slug,
              weight: fontFace.weight,
              style: fontFace.style,
            }),
          ),
        );

        return {};
      })(),
    );
  }

  private async loadRemoteFontFace(RemoteFontFace: RemoteFontFace) {
    const { familySlug, faceSlug, weight, style } = RemoteFontFace;

    const resp = await fetch(
      `https://dplsljucua5he.cloudfront.net/${encodeURIComponent(faceSlug)}`,
    );
    const scrambledArrayBuffer = await resp.arrayBuffer();

    const scrambledBytes = new Uint8Array(scrambledArrayBuffer);
    const unscrambledBytes = new Uint8Array(scrambledBytes.length);
    let a = 42 ^ (scrambledBytes.byteLength & 0xff);
    for (let i = 0; i < scrambledBytes.length; i++) {
      const b = scrambledBytes[i];
      unscrambledBytes[i] = b ^ a;
      a = (37 + 11 * a + 13 * (b ^ a)) & 0xff;
    }
    const unscrambledArrayBuffer = new Uint8Array(unscrambledBytes).buffer;

    const font = new FontFace(familySlug, unscrambledArrayBuffer, {
      style,
      weight,
    });

    await font.load();
    document.fonts.add(font);
  }
}

export const globalRemoteFontStore = new RemoteFontStore();

class FontOverrideStore {
  private fontOverrides = observable.map<string, string>([], {
    deep: false,
  });

  setFontOverride(target: TypographyTarget, fontFamilySlug: string) {
    this.fontOverrides.set(target, fontFamilySlug);
  }

  clearFontOverride(target: TypographyTarget) {
    this.fontOverrides.delete(target);
  }

  getFontOverride(target: TypographyTarget) {
    return this.fontOverrides.get(target);
  }
}

export const globalFontOverrideStore = new FontOverrideStore();

export const HEADING_FONT_COLLECTIONS = {
  'brett-favorites': {
    isNew: true,
    name: 'Brett @ Designjoy',
    image: BrettCollectionImage,
    descriptionHeading: 'Picked by Brett @ Designjoy',
    description: (
      <span>
        Founder of <Link href="https://www.designjoy.co">Designjoy</Link>, a $1M
        one-man design studio.
      </span>
    ),
  },
  'dann-favorites': {
    isNew: true,
    name: 'Dann Petty',
    image: DannCollectionImage,
    descriptionHeading: 'Picked by Dann Petty',
    description: (
      <span>
        World's fastest web designer and founder of{' '}
        <Link href="https://designfulltime.com/">designfulltime.com</Link>.
      </span>
    ),
  },
  'fons-favorites': {
    isNew: true,
    name: 'Fons Mans',
    image: FonsCollectionImage,
    descriptionHeading: 'Picked by Fons Mans',
    description: (
      <span>
        Founder of Design Studio{' '}
        <Link href="https://offgrid.inc/">Offgrid.inc</Link>.
      </span>
    ),
  },
  'ayush-favorites': {
    isNew: true,
    name: 'Ayush Soni',
    image: AyushCollectionImage,
    descriptionHeading: 'Picked by Ayush Soni',
    description: (
      <span>
        Creative director & founder{' '}
        <Link href="https://www.hex.inc/">Hex.Inc</Link>. A full-cycle
        (experimental) brand & design studio.
      </span>
    ),
  },
  'staff-favorites': {
    name: 'Staff favorites',
    descriptionHeading: 'From us to you',
    description:
      'A handpicked collection of our favorite newly discovered fonts. Try them out on your next project!',
    image: StaffFavoritesImage,
    isNew: false,
    tagline: 'Staff favorite',
  },
  edgy: {
    name: 'Edgy',
    descriptionHeading: 'Recommended for',
    description:
      'This style is perfect for agencies or clients looking to stand out as the new kid on the block, approaching things in a bold, disruptive way.',
    image: EdgyImage,
    isNew: false,
    tagline: 'Edgy collection',
  },
  elegant: {
    name: 'Elegant',
    descriptionHeading: 'Recommended for',
    description:
      'This style is perfect for brands or clients aiming for a feminine, refined look, bringing a touch of elegance and sophistication to their website.',
    image: ElegantImage,
    isNew: false,
    tagline: 'Elegant collection',
  },
  clean: {
    name: 'Clean',
    descriptionHeading: 'Recommended for',
    description:
      'This style is perfect for tech companies and startups seeking a clean, minimal look with wide, readable fonts and circular shapes.',
    image: CleanImage,
    isNew: false,
    tagline: 'Clean collection',
  },
  rounded: {
    name: 'Rounded',
    descriptionHeading: 'Recommended for',
    description:
      'Perfect for brands like childcares, meetups, or anything lighthearted, this style uses playful, rounded fonts for a welcoming feel.',
    image: RoundedImage,
    isNew: false,
    tagline: 'Rounded collection',
  },
  compressed: {
    name: 'Compressed',
    descriptionHeading: 'Recommended for',
    description:
      'Perfect for content-heavy sites like blogs, this style offers a sleek, space-efficient design while maintaining readability.',
    isNew: false,
    image: CompressedImage,
    tagline: 'Compressed collection',
  },
  quirky: {
    name: 'Quirky',
    descriptionHeading: 'Recommended for',
    description:
      'Perfect for brands that want to stand out and leave a fun memory, this quirky style works well for niche cafes, bakeries, etc.',
    image: QuirkyImage,
    isNew: false,
    tagline: 'Quirky collection',
  },
  og: {
    name: 'OG',
    descriptionHeading: 'Recommended for',
    description:
      'Perfect for corporate sites or anyone seeking a safer option, these OG fonts are timeless and trusted, offering reliable choices for any brand.',
    image: OGImage,
    isNew: false,
    tagline: 'OG collection',
  },
};

export type HeadingFontCollectionSlug = keyof typeof HEADING_FONT_COLLECTIONS;

export const HEADING_FONT_COLLECTION_SLUGS = Object.keys(
  HEADING_FONT_COLLECTIONS,
) as HeadingFontCollectionSlug[];

export const BODY_FONT_COLLECTION_SLUGS = ['body'] as const;

export const FONT_WEIGHTS_BODY = [400, 500];
export const FONT_WEIGHT_HEADING_REGULAR = 400;
export const FONT_WEIGHT_HEADING_MEDIUM = 500;
export const FONT_WEIGHT_HEADING_BOLD = 700;

export class FontMetadataService {
  private fontFamilyCache = observable.map<
    string,
    CacheState<FontFamilyMetadata>
  >([], {
    deep: false,
  });

  constructor(private blocksService: BlocksService) {}

  getFontFamily(slug: string) {
    const cached = this.fontFamilyCache.get(slug);
    if (cached) {
      return cached;
    }

    return addPromiseToCache(
      this.fontFamilyCache,
      slug,
      this.blocksService.getFontFamily(slug),
    );
  }

  async searchFontFamilies(
    query: string,
    tags: string[],
    abortSignal: AbortSignal,
    includePremiumFonts?: boolean,
  ) {
    const resp = await this.blocksService.searchFontFamilies(
      query,
      tags,
      abortSignal,
      includePremiumFonts,
    );
    for (const f of resp.results) {
      this.fontFamilyCache.set(f.slug, {
        type: 'fulfilled',
        value: f,
        promise: Promise.resolve(f),
      });
    }
    return resp;
  }

  async getSearchFontFamiliesPage(url: string) {
    const resp = await this.blocksService.getSearchFontFamiliesPage(url);
    for (const f of resp.results) {
      this.fontFamilyCache.set(f.slug, {
        type: 'fulfilled',
        value: f,
        promise: Promise.resolve(f),
      });
    }
    return resp;
  }

  async listRecommendedFontFamilies(
    ...args: Parameters<BlocksService['listRecommendedFontFamilies']>
  ) {
    const results = await this.blocksService.listRecommendedFontFamilies(
      ...args,
    );

    for (const { fontFamily: f } of results) {
      this.fontFamilyCache.set(f.slug, {
        type: 'fulfilled',
        value: f,
        promise: Promise.resolve(f),
      });
    }

    return results;
  }

  async listFontFamiliesFromCollections(
    collections: ReadonlyArray<string>,
    count?: number,
    tags?: string[],
  ) {
    const results = await this.blocksService.listFontFamiliesFromCollections(
      collections,
      count,
      tags,
    );
    for (const { fontFamily: f } of results) {
      this.fontFamilyCache.set(f.slug, {
        type: 'fulfilled',
        value: f,
        promise: Promise.resolve(f),
      });
    }
    return results;
  }

  async getFontCollection(slug: string) {
    const results = await this.blocksService.getFontCollection(slug);

    for (const f of results.fontFamilies) {
      this.fontFamilyCache.set(f.slug, {
        type: 'fulfilled',
        value: f,
        promise: Promise.resolve(f),
      });
    }
    return results;
  }
}
